import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    currentOrganization,
    isLoadingOrgs,
    needsOrgSelection,
    Organization,
    organizations,
} from '../state/organization';

export interface OrganizationsResponse {
    organizations: Organization[];
    error?: string;
}

export interface SelectOrganizationResponse {
    success: boolean;
    organization: Organization;
    error?: string;
}

/**
 * Fetches the list of organizations the user belongs to
 */
export const fetchOrganizations = async (): Promise<Organization[]> => {
    isLoadingOrgs.value = true;

    try {
        const response = await fetch('/api/organizations', {
            method: 'GET',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            console.error(
                '[ORG-SERVICE] Failed to fetch organizations:',
                errorData.error,
            );
            throw new Error(errorData.error || 'Failed to fetch organizations');
        }

        const data: OrganizationsResponse = await response.json();
        const orgs = data.organizations || [];

        organizations.value = orgs;
        return orgs;
    } finally {
        isLoadingOrgs.value = false;
    }
};

/**
 * Selects an organization for the current session
 */
export const selectOrganization = async (
    organizationId: number,
): Promise<Organization> => {
    const response = await fetch('/api/organizations/select', {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify({ organizationId }),
    });

    if (!response.ok) {
        const errorData = await response.json();
        console.error('[ORG-SERVICE] Failed to select organization:', errorData.error);
        throw new Error(errorData.error || 'Failed to select organization');
    }

    const data: SelectOrganizationResponse = await response.json();

    if (data.success && data.organization) {
        currentOrganization.value = data.organization;
        needsOrgSelection.value = false;
    }

    return data.organization;
};

/**
 * Handles organization selection logic after authentication
 * - If 0 orgs or API unavailable: skip selection (feature not ready)
 * - If 1 org: auto-select, no modal
 * - If multiple orgs + lastUsedOrgId: auto-select last used
 * - If multiple orgs + no lastUsedOrgId: show selector modal
 */
export const handleOrganizationSelection = async (
    lastUsedOrgId?: number,
): Promise<void> => {
    try {
        const orgs = await fetchOrganizations();

        if (orgs.length === 0) {
            // No organizations returned - API might not be ready yet
            // Skip organization selection and let app continue normally
            return;
        }

        if (orgs.length === 1) {
            await selectOrganization(orgs[0].id);
            return;
        }

        // Multiple organizations
        if (lastUsedOrgId) {
            const lastUsedOrg = orgs.find((org) => org.id === lastUsedOrgId);
            if (lastUsedOrg) {
                await selectOrganization(lastUsedOrgId);
                return;
            }
            // Last used org no longer available, clear it
            console.warn(
                '[ORG-SERVICE] Last used organization not found, prompting selection',
            );
        }

        needsOrgSelection.value = true;
    } catch (error) {
        // API failed - skip organization selection and let app continue
        console.warn('[ORG-SERVICE] Organization API not available, skipping:', error);
    }
};

// React Query hooks

export const useOrganizations = () => {
    return useQuery({
        queryKey: ['organizations'],
        queryFn: fetchOrganizations,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useSelectOrganization = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: selectOrganization,
        onSuccess: () => {
            // Invalidate relevant queries after org change
            queryClient.invalidateQueries();
        },
    });
};
